import React, { ComponentProps, FC, ReactNode, useCallback, useState } from 'react';

import { EvIssueDetailsNew, EvToast, INoteConfig, TOAST_STATUS } from '@evinced-private/ui-common';

import { mapPlatformIssueDetailsToReport } from 'src/common/components/details-block/DetailsBlockHelper';
import { TicketsSystemsHeaderActions } from 'src/common/components/tickets-systems/TicketsSystemsHeaderActions';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { ClipboardHelper } from 'src/common/helpers/ClipboardHelper';
import { getBaseURL } from 'src/common/helpers/UrlNormalizingHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { Logger } from 'src/common/services/Logger';
import { screenshotService } from 'src/common/services/ScreenshotService';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';
import { PlatformIssueDetails } from 'src/common/types/IssueDetailsType';

interface IIssueDetailsNewModalProps {
	// The raw query result, used for the Jira/Azure ticket
	issue: PlatformIssueDetails | undefined;
	// The fields shown in the dialog (each product picks its own)
	detailsIssue: ComponentProps<typeof EvIssueDetailsNew>['issue'];
	product: ProductType;
	issuePageUrl: string;
	isLoading: boolean;
	isFullPage?: boolean;
	onClose?: () => void;
	copyToClipboardButton: (textToCopy: string) => ReactNode;
	noteConfig?: INoteConfig;
}

/** The new issue details dialog (or full page) shared by the WFA and Web SDK details blocks. */
export const IssueDetailsNewModal: FC<IIssueDetailsNewModalProps> = ({
	issue,
	detailsIssue,
	product,
	issuePageUrl,
	isLoading,
	isFullPage,
	onClose,
	copyToClipboardButton,
	noteConfig
}) => {
	const { isDefaultJiraLabelsEnabled } = usePlatformConfigStore();
	const [showCopyLinkToast, setShowCopyLinkToast] = useState(false);
	const [copyLinkSucceeded, setCopyLinkSucceeded] = useState(true);

	const openIssueInNewTab = useCallback((): void => {
		window.open(issuePageUrl, '_blank');
	}, [issuePageUrl]);

	const onCopyLinkClick = useCallback((): void => {
		setCopyLinkSucceeded(ClipboardHelper.copyToClipboard(`${getBaseURL()}${issuePageUrl}`));
		setShowCopyLinkToast(true);
	}, [issuePageUrl]);

	return (
		<>
			{/* Mounted only while shown: EvToast reads `show` once, on mount */}
			{showCopyLinkToast && (
				<EvToast
					announcement={copyLinkSucceeded ? 'Copied to Clipboard' : 'Copy Attempt Error'}
					status={copyLinkSucceeded ? TOAST_STATUS.SUCCESS : TOAST_STATUS.FAIL}
					onToastClose={() => setShowCopyLinkToast(false)}
				/>
			)}
			<TicketsSystemsHeaderActions
				issue={mapPlatformIssueDetailsToReport(issue, isDefaultJiraLabelsEnabled, product)}
			>
				{(ticketsSystemsHeaderProps) => (
					<EvIssueDetailsNew
						// TODO: remove once ui-common ships the EvEllipsedTextWithTooltip fix. Its title
						// keeps the first value it mounts with ('' while loading), so remount on load.
						key={isLoading ? 'loading' : 'loaded'}
						// The severity badge only knows the default names, not a tenant's custom labels
						issue={{ ...detailsIssue, severity: issue?.defaultSeverity }}
						isOpen
						onClose={onClose ?? (() => undefined)}
						isFullPage={isFullPage}
						appElement={PLATFORM_APP_ID}
						onOpenInNewTab={isFullPage ? undefined : openIssueInNewTab}
						onCopyLinkClick={onCopyLinkClick}
						isLoading={isLoading}
						screenshotService={screenshotService}
						logger={Logger}
						copyToClipboardButton={copyToClipboardButton}
						noteConfig={noteConfig}
						{...ticketsSystemsHeaderProps}
					/>
				)}
			</TicketsSystemsHeaderActions>
		</>
	);
};
